// A dashboard as a JSON file: what is written on export and how it is read back on import.
// Pure helpers, no React.
import { DASHBOARD_FILE_KIND, dashboardFileSchema, type Dashboard, type DashboardDoc, type DashboardFile, type Signal } from '@twinlink/shared';

type SignalRef = Pick<Signal, 'id' | 'key' | 'name'> & { links?: { wellId: string | null } };

const signalIds = (doc: DashboardDoc) => [...new Set(doc.pages.flatMap((p) => Object.values(p.widgets).flatMap((w) => w.config.series.flatMap((s) => (s.binding.kind === 'signal' ? [s.binding.signalId] : [])))))];

/** The file of a dashboard. `signals` names the signals it uses, so another organization can find its own. */
export function buildDashboardFile(orgId: string, dashboard: Pick<Dashboard, 'name' | 'description' | 'doc'>, signals: SignalRef[], now = new Date()): DashboardFile {
  const byId = new Map(signals.map((s) => [s.id, s]));
  return {
    kind: DASHBOARD_FILE_KIND,
    formatVersion: 1,
    exportedAt: now.toISOString(),
    orgId,
    name: dashboard.name,
    description: dashboard.description,
    doc: dashboard.doc,
    signals: signalIds(dashboard.doc).flatMap((id) => {
      const s = byId.get(id);
      return s ? [{ id, key: s.key, name: s.name }] : [];
    }),
  };
}

export const dashboardFileName = (name: string) => `${name.trim().replace(/[^\p{L}\p{N}]+/gu, '-').replace(/^-+|-+$/g, '').slice(0, 80) || 'dashboard'}.json`;

/** Null when the text is not a dashboard file. */
export function parseDashboardFile(text: string): DashboardFile | null {
  try {
    const parsed = dashboardFileSchema.safeParse(JSON.parse(text.charCodeAt(0) === 0xfeff ? text.slice(1) : text));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

export interface ImportPlan {
  doc: DashboardDoc;
  /** Signals of the file, and how many of them exist here. */
  total: number;
  found: number;
  /** Names of the signals that were not found. */
  missing: string[];
  /** The file comes from another organization: its images and schematics do not exist here. */
  foreign: boolean;
}

/**
 * Points the document at the signals of this organization. A signal with the same id is kept;
 * otherwise the one with the same key, then the same name, is taken — when several have it, the
 * one of the chosen well. What is not found is left as it is: the widget says the signal is
 * missing until another one is chosen.
 */
export function planImport(file: DashboardFile, orgId: string, signals: SignalRef[], wellId: string | null): ImportPlan {
  const here = new Set(signals.map((s) => s.id));
  const refs = new Map(file.signals.map((s) => [s.id, s]));
  const pick = (candidates: SignalRef[]) => {
    if (candidates.length === 1) return candidates[0]!.id;
    const ofWell = wellId ? candidates.filter((s) => s.links?.wellId === wellId) : [];
    return ofWell.length === 1 ? ofWell[0]!.id : null;
  };
  const target = new Map<string, string | null>();
  for (const id of signalIds(file.doc)) {
    if (here.has(id)) {
      target.set(id, id);
      continue;
    }
    const ref = refs.get(id);
    target.set(id, ref ? (pick(signals.filter((s) => s.key === ref.key)) ?? pick(signals.filter((s) => s.name === ref.name))) : null);
  }
  const foreign = !!file.orgId && file.orgId !== orgId;
  const doc: DashboardDoc = {
    ...file.doc,
    pages: file.doc.pages.map((page) => ({
      ...page,
      widgets: Object.fromEntries(
        Object.entries(page.widgets).map(([id, w]) => [
          id,
          {
            ...w,
            config: {
              ...w.config,
              ...(foreign && w.config.fileId ? { fileId: null } : {}),
              ...(foreign && w.config.schematicId ? { schematicId: null } : {}),
              series: w.config.series.map((s) => {
                const next = s.binding.kind === 'signal' ? target.get(s.binding.signalId) : null;
                return next && s.binding.kind === 'signal' ? { ...s, binding: { ...s.binding, signalId: next } } : s;
              }),
            },
          },
        ]),
      ),
    })),
  };
  const missing = [...target].filter(([, to]) => !to).map(([id]) => refs.get(id)?.name ?? id);
  return { doc, total: target.size, found: target.size - missing.length, missing, foreign };
}
